import { cx } from '../ui/cx.ts';
import { useLocale, useT } from './context.tsx';
import { LOCALE_NATIVE_LABELS, SUPPORTED_LOCALES } from './types.ts';

/**
 * Compact header control for flipping between `zh-CN` and `en`. Renders
 * as a pill cluster on a recessed `well` — the active locale lights
 * with the signal accent (Dispatch Deck restyle, epic #897).
 *
 * Design rationale: a dropdown feels heavier than necessary for two
 * options, and the click target stays obvious even on narrow headers.
 * DESIGN.md tokens only — no hex literals, no arbitrary px sizes.
 *
 * Label resolution uses `LOCALE_NATIVE_LABELS` (exported from `types.ts`)
 * so every option always renders in its own script regardless of the
 * active locale. These labels are intentionally NOT in the translation
 * dictionaries — they are UI chrome, not content.
 *
 * The persisted choice survives page reloads via `localStorage` (see
 * `context.tsx`); this component is a pure UI on top of that setter.
 */
export function LocaleSwitcher(): JSX.Element {
  const { locale, setLocale } = useLocale();
  const t = useT();
  return (
    <div
      role="group"
      aria-label={t('i18n.switcher.aria_label')}
      data-testid="locale-switcher"
      className="inline-flex items-center gap-0.5 rounded-lg border border-line-soft bg-well p-0.5"
    >
      {SUPPORTED_LOCALES.map((target) => {
        const isActive = locale === target;
        return (
          <button
            key={target}
            type="button"
            aria-pressed={isActive}
            data-testid={`locale-switcher-${target}`}
            onClick={() => {
              if (!isActive) {
                setLocale(target);
              }
            }}
            className={cx(
              'rounded px-2 py-1 text-micro font-medium transition-colors duration-120 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal/40',
              isActive ? 'bg-signal-dim text-signal' : 'text-paper-low hover:text-paper',
            )}
          >
            {LOCALE_NATIVE_LABELS[target]}
          </button>
        );
      })}
    </div>
  );
}
